import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';

// Erstelle den Kontext
const ContextMenuContext = React.createContext();

// Hook zur Verwendung des Kontextes in Komponenten
export const useContextMenu = () => {
  return useContext(ContextMenuContext);
};

// Kontextanbieter-Komponente
export const ContextMenuProvider = ({ children }) => {
  const [isContextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuData, setContextMenuData] = useState({identifier: null, type: '' });
  const [contextMenuPosition, setContextMenuPosition] = useState({x: 0, y: 0})
  const [contextMenuOffset, setOffset] = useState({ width: 0, height: 0 });

  const contextMenuPositionRef = useRef(contextMenuPosition);
  contextMenuPositionRef.current = contextMenuPosition;

  const setPosition = useCallback((x, y) => {
    const menuWidth = contextMenuOffset.width;
    const menuHeight = contextMenuOffset.height;

    // Viewport-Koordinaten (clientX/clientY) -> an Viewport-Grenzen clampen.
    const screenWidth = window.innerWidth - 20;
    const screenHeight = window.innerHeight - 20;

    if (x + menuWidth > screenWidth) {
      x = screenWidth - menuWidth;
    }

    if (y + menuHeight > screenHeight) {
      y = screenHeight - menuHeight;
    }

    setContextMenuPosition({ x, y });
  }, [contextMenuOffset]);

  // Re-clamp position when menu dimensions are measured; ref avoids reactive dep on position itself.
  useEffect(() => {
    const { x, y } = contextMenuPositionRef.current;
    setPosition(x, y);
  }, [contextMenuOffset, setPosition]);

  // Funktion zum Öffnen des Kontextmenüs
  const openContextMenu = useCallback((event, identifier, type) => {
    event.preventDefault();
    if (event.touches) {
      setPosition(event.touches[0].clientX, event.touches[0].clientY);
    } else {
      setPosition(event.clientX, event.clientY);
    }
    setContextMenuData({ identifier, type });
    setContextMenuOpen(true);
  }, [setPosition]);

  // Funktion zum Schließen des Kontextmenüs
  const closeContextMenu = useCallback(() => {
    setContextMenuOpen(false);
  }, []);

  const setContextMenuOffset = useCallback((width, height) => {
    setOffset({width, height});
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isContextMenuOpen && event.target.innerHTML !== 'settings') {
        closeContextMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isContextMenuOpen, closeContextMenu]);

  return (
    <ContextMenuContext.Provider value={{ isContextMenuOpen, contextMenuData, contextMenuPosition, setContextMenuOffset, openContextMenu, closeContextMenu }}>
      <div>
        {children}
      </div>
    </ContextMenuContext.Provider>
  );
};